/**
 * User management — who has a login, at what role, tied to which employee.
 *
 * The column that matters most is **Employee ID**, because for a VP, manager or
 * marketing employee it is what limits the account to their own hierarchy. An
 * account in one of those roles with no id resolves to no person, and its owner
 * sees empty dashboards that look exactly like a data outage. So that condition
 * is counted in the header and flagged on the row rather than left to be
 * discovered from a support call.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle, Link2Off, Plus, ShieldCheck, UserCog, UserMinus, UserPlus,
} from "lucide-react";

import { api } from "@/api/client";
import { KpiCard } from "@/components/cards/KpiCard";
import { DataTable, type Column } from "@/components/tables/DataTable";
import { UserEditor } from "@/features/admin/UserEditor";
import { useAuthStore } from "@/store/auth";
import {
  HIERARCHY_ROLES, ROLE_LABEL, type ManagedUser, type Role,
  type UserListResponse,
} from "@/types/api";
import { count } from "@/utils/format";

const ROLES: Role[] = ["ADMIN", "MANAGEMENT", "VP", "MANAGER", "EMPLOYEE"];

const ROLE_STYLE: Record<Role, { color: string; bg: string }> = {
  ADMIN: { color: "#9E3B2E", bg: "#FBECE9" },
  MANAGEMENT: { color: "#0B5D57", bg: "#E3EFED" },
  VP: { color: "#2563C9", bg: "#E8F0FC" },
  MANAGER: { color: "#7A5AF8", bg: "#F1EEFE" },
  EMPLOYEE: { color: "#6B6459", bg: "#F0EEEA" },
};

function relative(iso: string | null): string {
  if (!iso) return "never";
  const days = Math.round((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.round(days / 30)}mo ago`;
  return `${Math.round(days / 365)}y ago`;
}

export function UserManagement() {
  const me = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const [role, setRole] = useState("");
  const [active, setActive] = useState("");
  const [search, setSearch] = useState("");
  const [unlinked, setUnlinked] = useState(false);
  const [editing, setEditing] = useState<ManagedUser | "new" | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-users", role, active, search, unlinked],
    queryFn: async () => {
      const { data } = await api.get<UserListResponse>("/admin/users", {
        params: {
          role: role || undefined,
          active: active || undefined,
          q: search || undefined,
          unlinked: unlinked ? "true" : undefined,
        },
      });
      return data;
    },
    staleTime: 15_000,
  });

  const toggleActive = useMutation({
    mutationFn: async (user: ManagedUser) => {
      const { data } = await api.patch<ManagedUser>(
        `/admin/users/${user.id}`, { is_active: !user.is_active });
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
  });

  const rows = data?.results ?? [];
  const summary = data?.summary;

  const columns = useMemo<Column<ManagedUser>[]>(() => [
    {
      key: "username", header: "Login", width: "200px",
      render: (u) => (
        <span className="min-w-0">
          <span className="flex items-center gap-1.5">
            <span className="font-medium truncate">{u.username}</span>
            {!u.is_active && (
              <span className="chip border-transparent px-1 py-0 bg-surface-alt
                               text-ink-faint text-[10.5px]">disabled</span>
            )}
            {u.id === me?.id && (
              <span className="chip border-transparent px-1 py-0 bg-petrol-soft
                               text-petrol text-[10.5px]">you</span>
            )}
          </span>
          {u.display_name && (
            <span className="block text-[11.5px] text-ink-faint truncate">
              {u.display_name}
            </span>
          )}
        </span>
      ),
    },
    {
      key: "role", header: "Role", width: "150px",
      render: (u) => (
        <span className="chip border-transparent"
              style={{ color: ROLE_STYLE[u.role].color,
                       background: ROLE_STYLE[u.role].bg }}>
          {ROLE_LABEL[u.role]}
        </span>
      ),
    },
    {
      key: "employee_id", header: "Employee ID", width: "190px",
      render: (u) => {
        // The condition worth surfacing: a hierarchy role with nothing behind
        // it. The owner sees empty dashboards and reports a data bug.
        if (!u.employee_id) {
          return HIERARCHY_ROLES.includes(u.role)
            ? (
              <span className="inline-flex items-center gap-1 text-bad text-[12px]">
                <Link2Off size={11} /> not linked
              </span>
            )
            : <span className="text-ink-faint">—</span>;
        }
        return (
          <span className="min-w-0">
            <span className="font-mono text-[12px] block truncate">
              {u.employee_id}
            </span>
            {u.resolved_name && u.resolved_name !== u.display_name && (
              <span className="block text-[11px] text-ink-faint truncate">
                {u.resolved_name}
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: "person_key", header: "Person", width: "110px",
      render: (u) => (u.person_key
        ? <span className="font-mono text-[11.5px] text-ink-muted">{u.person_key}</span>
        : <span className="text-ink-faint">—</span>),
    },
    {
      key: "scope_count", header: "Extra scope", align: "right",
      render: (u) => (u.scope_count
        ? <span className="tnum">{u.scope_count}</span>
        : <span className="text-ink-faint">—</span>),
      value: (u) => u.scope_count,
    },
    {
      key: "may_see_financials", header: "Permissions", width: "150px",
      render: (u) => (
        <span className="flex flex-wrap gap-1">
          {u.may_see_financials && <Perm label="₹" title="May see financials" />}
          {u.may_see_patient_pii && <Perm label="PII" title="May see patient identifiers" warn />}
          {u.may_export && <Perm label="export" title="May export" />}
        </span>
      ),
    },
    {
      key: "last_login", header: "Last seen", align: "right", width: "110px",
      render: (u) => (
        <span className={u.last_login ? "" : "text-ink-faint"}>
          {relative(u.last_login)}
        </span>
      ),
      value: (u) => (u.last_login ? new Date(u.last_login).getTime() : 0),
    },
    {
      key: "actions", header: "", align: "right", width: "72px",
      // `value` returns null so the CSV export carries an empty column rather
      // than the string "[object Object]".
      value: () => null,
      render: (u) => (
        <span className="flex items-center gap-1 justify-end">
          <button
            onClick={(e) => { e.stopPropagation(); setEditing(u); }}
            title="Edit this account"
            className="p-1 rounded hover:bg-surface-alt text-ink-muted"
          >
            <UserCog size={14} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); toggleActive.mutate(u); }}
            disabled={u.id === me?.id}
            title={u.id === me?.id
              ? "You cannot disable your own account"
              : u.is_active ? "Disable this account" : "Enable this account"}
            className="p-1 rounded hover:bg-surface-alt text-ink-muted
                       disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {u.is_active ? <UserMinus size={14} /> : <UserPlus size={14} />}
          </button>
        </span>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [me?.id, toggleActive]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-[24px] tracking-tight">Users</h1>
          <p className="text-[13.5px] text-ink-muted mt-0.5 max-w-3xl">
            A login is not the same thing as an employee. The{" "}
            <strong>employee id</strong> is what connects the two, and for a VP,
            manager or marketing employee it is what limits the account to their
            own team's numbers.
          </p>
        </div>
        <button onClick={() => setEditing("new")} className="btn btn-primary">
          <Plus size={14} /> New user
        </button>
      </div>

      <div className="grid gap-3"
           style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 190px), 1fr))" }}>
        <KpiCard loading={isLoading} label="Accounts" value={count(summary?.total)} />
        <KpiCard loading={isLoading} label="Active" value={count(summary?.active)} />
        <KpiCard loading={isLoading} label="Awaiting approval"
                 value={count(summary?.pending_requests)} />
        <KpiCard loading={isLoading} label="Not linked to a person"
                 value={count(summary?.unlinked_hierarchy_accounts)}
                 higherIsBetter={false} />
      </div>

      {!!summary?.unlinked_hierarchy_accounts && (
        <button
          onClick={() => { setUnlinked(true); setRole(""); }}
          className="w-full flex items-start gap-2 text-left px-3 py-2.5 rounded-md
                     border border-warn/30 bg-warn-soft text-[12.5px]"
        >
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warn" />
          <span>
            <strong>{summary.unlinked_hierarchy_accounts}</strong> account
            {summary.unlinked_hierarchy_accounts === 1 ? " is" : "s are"} in a
            hierarchy role with no employee id. Those people see empty
            dashboards — which reads as a data outage, not a settings problem.
            Click to filter.
          </span>
        </button>
      )}

      <div className="flex flex-wrap gap-3 items-end">
        <label className="flex flex-col gap-1 flex-1 min-w-[220px]">
          <span className="label">Search</span>
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="username, name, employee id or email"
            className="text-sm bg-surface border border-rule rounded-md px-2.5 py-1.5"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="label">Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value)}
                  className="text-sm bg-surface border border-rule rounded-md px-2 py-1.5">
            <option value="">All</option>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
                {summary?.by_role?.[r] !== undefined ? ` (${summary.by_role[r]})` : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="label">Status</span>
          <select value={active} onChange={(e) => setActive(e.target.value)}
                  className="text-sm bg-surface border border-rule rounded-md px-2 py-1.5">
            <option value="">All</option>
            <option value="true">Active</option>
            <option value="false">Disabled</option>
          </select>
        </label>
        {unlinked && (
          <button onClick={() => setUnlinked(false)}
                  className="btn text-xs">clear "not linked"</button>
        )}
      </div>

      <DataTable<ManagedUser>
        rows={rows}
        columns={columns}
        rowKey={(u) => String(u.id)}
        onRowClick={(u) => setEditing(u)}
        exportName="users"
        loading={isLoading}
        emptyMessage="No accounts match these filters."
        footnote="Click a row to change role, employee id, permissions or scope. Every change is written to the audit log."
      />

      {toggleActive.isError && (
        <p className="text-[12.5px] text-bad bg-bad-soft border border-bad/25
                      rounded-md px-3 py-2">
          {(toggleActive.error as { response?: { data?: { detail?: string } } })
            ?.response?.data?.detail ?? "Could not change that account."}
        </p>
      )}

      <p className="flex items-start gap-2 text-[12px] text-ink-faint">
        <ShieldCheck size={13} className="mt-0.5 shrink-0" />
        Roles decide which rows someone can read. Extra scope grants widen that
        within the role's ceiling — they never turn a manager into management,
        and they never confer the ability to administer users.
      </p>

      {editing && (
        <UserEditor
          user={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
          }}
        />
      )}
    </div>
  );
}

function Perm({ label, title, warn }: { label: string; title: string; warn?: boolean }) {
  return (
    <span
      title={title}
      className={`chip px-1 py-0 text-[10.5px] ${
        warn ? "border-warn/30 bg-warn-soft text-warn"
             : "border-transparent bg-surface-alt text-ink-muted"}`}
    >
      {label}
    </span>
  );
}
